import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';

interface Booking {
  name: string;
  start: number | null | undefined;
  end: number | null | undefined;
  ID: string;
  y: number;
  isAvailable?: boolean;
}

interface BookingGroup {
  name: string;
  bookings: Booking[];
}

@Component({
  selector: 'app-gantt-chart',
  templateUrl: './gantt-chart.component.html',
  styleUrls: ['./gantt-chart.component.css']
})
export class GanttChartComponent implements OnChanges {
  
  @Input() options: any = null;
  @Input() update: boolean = false;
  @Input() startDate: Date | null = null;  
  @Input() endDate: Date | null = null; 
 @Output() availabilityChanged = new EventEmitter<any[]>();
  private rawBookings: Booking[] = [];
  bookingGroups: BookingGroup[] = [];
  
  currentScale: 'year' | 'month' | 'week' = 'month';
  displayColumns: any[] = [];
  
  currentDisplayDate: Date = new Date();
  currentPeriodTitle: string = '';

  public originalStartDate: Date | null = null;
  public originalEndDate: Date | null = null;
  public hasIntersections: boolean = false;

  // Проверка пересечения бронирования с периодом
  isBookingIntersectWithPeriod(booking: Booking): boolean {
    if (!this.originalStartDate || !this.originalEndDate) return false;
    if (!booking.start || !booking.end) return false;
    
    const bookingStart = new Date(booking.start);
    const bookingEnd = new Date(booking.end);
    const periodStart = new Date(this.originalStartDate);
    const periodEnd = new Date(this.originalEndDate);
    
    return bookingStart <= periodEnd && bookingEnd >= periodStart;
  }
  
private updateAvailabilityFlags(): void {
  if (!this.originalStartDate || !this.originalEndDate) {
    this.hasIntersections = false;
    return;
  }
  
  let hasIntersection = false;
  const availabilityData = []; // Массив для передачи в родитель
  
  for (const group of this.bookingGroups) {
    for (const booking of group.bookings) {
      if (this.isBookingIntersectWithPeriod(booking)) {
        booking.isAvailable = false;
        hasIntersection = true;
      } else {
        booking.isAvailable = true;
      }
      
      // Добавляем информацию о доступности
      availabilityData.push({
        ID: booking.ID,
        name: booking.name,
        isAvailable: booking.isAvailable
      });
    }
  }
  
  this.hasIntersections = hasIntersection;
  
  // Отправляем данные в родительский компонент
  this.availabilityChanged.emit(availabilityData);
}
  
  // Проверка пересекается ли с периодом
  hasGroupIntersection(group: BookingGroup): boolean {
    if (!this.originalStartDate || !this.originalEndDate) return false;
    
    const periodStart = new Date(this.originalStartDate);
    const periodEnd = new Date(this.originalEndDate);
    
    for (const booking of group.bookings) {
      if (booking.start && booking.end) {
        const bookingStart = new Date(booking.start);
        const bookingEnd = new Date(booking.end);
        
        if (bookingStart <= periodEnd && bookingEnd >= periodStart) {
          return true;
        }
      }
    }
    return false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && this.options) {
      this.parseHighchartsOptions();
    }
    if (changes['update'] && this.update) {
      this.refreshChart();
    }
    
    if (changes['startDate'] && this.startDate) {
      this.originalStartDate = this.startDate;
      this.updateAvailabilityFlags();
      this.refreshChart();
    }
    if (changes['endDate'] && this.endDate) {
      this.originalEndDate = this.endDate;
      this.updateAvailabilityFlags();
      this.refreshChart();
    }
  }

  private parseHighchartsOptions(): void {
    if (this.options && this.options.series && this.options.series[0]) {
      const seriesData = this.options.series[0].data || [];
      
      this.rawBookings = seriesData.map((item: any) => {
        let start = item.start;
        let end = item.end;
        
        if (!start || start === 0) start = null;
        if (!end || end === 0) end = null;
        
        return {
          name: item.name || item.title || 'Без названия',
          start: start,
          end: end,
          ID: item.id || item.ID || '',
          y: item.y || 0,
          isAvailable: true
        };
      });
      
      this.groupBookingsByName();
      this.updateAvailabilityFlags();
      
      this.refreshChart();
      this.originalStartDate = this.startDate;
      this.originalEndDate = this.endDate;
      this.goToday();
    }
  }
  
  private groupBookingsByName(): void {
    const groupsMap = new Map<string, Booking[]>();
    
    for (const booking of this.rawBookings) {
      if (!groupsMap.has(booking.name)) {
        groupsMap.set(booking.name, []);
      }
      groupsMap.get(booking.name)!.push(booking);
    }
    
    this.bookingGroups = Array.from(groupsMap.entries()).map(([name, bookings]) => ({
      name: name,
      bookings: bookings
    }));
  }

  private refreshChart(): void {
    this.generateDisplayColumns();
  }

  // Навигация
  goPrev(): void {
    switch (this.currentScale) {
      case 'year':
        this.currentDisplayDate.setFullYear(this.currentDisplayDate.getFullYear() - 1);
        break;
      case 'month':
        this.currentDisplayDate.setMonth(this.currentDisplayDate.getMonth() - 1);
        break;
      case 'week':
        this.currentDisplayDate.setDate(this.currentDisplayDate.getDate() - 7);
        break;
    }
    this.generateDisplayColumns();
  }
  
  goNext(): void {
    switch (this.currentScale) {
      case 'year':
        this.currentDisplayDate.setFullYear(this.currentDisplayDate.getFullYear() + 1);
        break;
      case 'month':
        this.currentDisplayDate.setMonth(this.currentDisplayDate.getMonth() + 1);
        break;
      case 'week':
        this.currentDisplayDate.setDate(this.currentDisplayDate.getDate() + 7);
        break;
    }
    this.generateDisplayColumns();
  }
  
  goToday(): void {
    if (this.originalStartDate && this.originalEndDate) {
      const centerDate = new Date((this.originalStartDate.getTime() + this.originalEndDate.getTime()) / 2);
      this.currentDisplayDate = centerDate;
    } else {
      this.currentDisplayDate = new Date();
    }
    this.generateDisplayColumns();
  }

  setYearScale(): void {
    this.currentScale = 'year';
    this.currentDisplayDate = new Date();
    this.generateDisplayColumns();
  }

  setMonthScale(): void {
    this.currentScale = 'month';
    this.currentDisplayDate = new Date();
    this.generateDisplayColumns();
  }

  setWeekScale(): void {
    this.currentScale = 'week';
    this.currentDisplayDate = new Date();
    this.generateDisplayColumns();
  }

  private generateDisplayColumns(): void {
    this.displayColumns = [];
    
    switch (this.currentScale) {
      case 'year':
        this.generateYearColumns();
        this.currentPeriodTitle = `${this.currentDisplayDate.getFullYear()} год`;
        break;
      case 'month':
        this.generateMonthColumns();
        const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 
                           'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
        this.currentPeriodTitle = `${monthNames[this.currentDisplayDate.getMonth()]} ${this.currentDisplayDate.getFullYear()}`;
        break;
      case 'week':
        this.generateWeekColumns();
        const weekStart = this.getWeekStart(this.currentDisplayDate);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        this.currentPeriodTitle = `${weekStart.getDate()}.${weekStart.getMonth() + 1} - ${weekEnd.getDate()}.${weekEnd.getMonth() + 1}.${weekEnd.getFullYear()}`;
        break;
    }
  }
  
  // Генерация колонок для года (12 месяцев)
  private generateYearColumns(): void {
    const year = this.currentDisplayDate.getFullYear();
    const monthNames = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 
                       'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
    
    for (let month = 0; month < 12; month++) {
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0);
      
      const bookingsInMonth: any[] = [];
      
      for (const group of this.bookingGroups) {
        for (const booking of group.bookings) {
          if (booking.start && booking.end) {
            const startDate = new Date(booking.start);
            const endDate = new Date(booking.end);
            
            if (startDate <= monthEnd && endDate >= monthStart) {
              const clippedStart = startDate < monthStart ? monthStart : startDate;
              const clippedEnd = endDate > monthEnd ? monthEnd : endDate;
              
              bookingsInMonth.push({ 
                start: clippedStart, 
                end: clippedEnd, 
                name: booking.name,
                group: group,
                booking: booking
              });
            }
          }
        }
      }
      
      this.displayColumns.push({
        label: monthNames[month],
        fullDate: monthStart,
        start: monthStart,
        end: monthEnd,
        type: 'month',
        bookings: bookingsInMonth
      });
    }
  }
  
  // Генерация колонок для месяца (дни)
  private generateMonthColumns(): void {
    const year = this.currentDisplayDate.getFullYear();
    const month = this.currentDisplayDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      
      this.displayColumns.push({
        label: day.toString(),
        fullDate: currentDate,
        start: currentDate,
        end: currentDate,
        type: 'day',
        isWeekend: currentDate.getDay() === 0 || currentDate.getDay() === 6
      });
    }
  }
  
  // Генерация колонок для недели (7 дней)
  private generateWeekColumns(): void {
    const weekStart = this.getWeekStart(this.currentDisplayDate);
    const weekdays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
    
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(weekStart);
      currentDay.setDate(weekStart.getDate() + i);
      
      this.displayColumns.push({
        label: `${weekdays[i]} ${currentDay.getDate()}`,
        fullDate: currentDay,
        start: currentDay,
        end: currentDay,
        type: 'day',
        isWeekend: i === 5 || i === 6
      });
    }
  }
  
  private getWeekStart(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  // Получение позиции бронирования внутри месяца (для года)
  getBookingPositionInMonth(bookingStart: Date, bookingEnd: Date, monthStart: Date, monthEnd: Date): { leftPercent: number; widthPercent: number } {
    const clippedStart = bookingStart < monthStart ? monthStart : bookingStart;
    const clippedEnd = bookingEnd > monthEnd ? monthEnd : bookingEnd;
    
    const daysInMonth = Math.ceil((monthEnd.getTime() - monthStart.getTime()) / (1000 * 3600 * 24)) + 1;
    const startDayOffset = Math.floor((clippedStart.getTime() - monthStart.getTime()) / (1000 * 3600 * 24));
    const duration = Math.floor((clippedEnd.getTime() - clippedStart.getTime()) / (1000 * 3600 * 24)) + 1;
    
    const leftPercent = (startDayOffset / daysInMonth) * 100;
    const widthPercent = (duration / daysInMonth) * 100;
    
    return { leftPercent, widthPercent };
  }
  
  getBookingsForMonth(monthColumn: any): any[] {
    return monthColumn.bookings || [];
  }
  
  getAllBookingsForRow(group: BookingGroup): Booking[] {
    return group.bookings.filter(b => b.start !== null);
  }
  
  // Стиль для полосы бронирования (месяц/неделя)
  getBookingStyle(booking: Booking): { left: string; width: string; display: string; backgroundColor?: string } {
    if (!booking.start) {
      return { left: '0%', width: '0%', display: 'none' };
    }
    
    const bookingStart = new Date(booking.start);
    const bookingEnd = booking.end ? new Date(booking.end) : new Date(booking.start);
    
    let startIndex = -1;
    let endIndex = -1;
    
    for (let i = 0; i < this.displayColumns.length; i++) {
      const colDate = this.displayColumns[i].start;
      const colDateEnd = this.displayColumns[i].end;
      
      const bookingStartDay = new Date(bookingStart);
      bookingStartDay.setHours(0, 0, 0, 0);
      const bookingEndDay = new Date(bookingEnd);
      bookingEndDay.setHours(23, 59, 59, 999);
     
      if (bookingStartDay <= colDateEnd && bookingEndDay >= colDate) {
        if (startIndex === -1) startIndex = i;
        endIndex = i;
      }
    }
    
    if (startIndex === -1) {
      return { left: '0%', width: '0%', display: 'none' };
    }
    
    const columnPercent = 100 / this.displayColumns.length;
    let leftPercent = startIndex * columnPercent;
    let widthPercent = (endIndex - startIndex + 1) * columnPercent;
    
    const isSameDay = booking.start === booking.end || !booking.end;
    if (isSameDay) {
      widthPercent = columnPercent * 0.7;
      leftPercent = leftPercent + (columnPercent * 0.15);
    }
    
    const backgroundColor = (booking.isAvailable === false) ? '#dc3545' : '#3156f8';
    
    return {
      left: `${leftPercent}%`,
      width: `${widthPercent}%`,
      display: 'flex',
      backgroundColor: backgroundColor
    };
  }
  
  formatTooltip(timestamp: number | null | undefined): string {
    if (!timestamp) return 'не указана';
    const date = new Date(timestamp);
    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
  }
  
  isCurrentPeriod(date: Date): boolean {
    if (!date) return false;
    const now = new Date();
    switch (this.currentScale) {
      case 'year':
        return date.getFullYear() === now.getFullYear();
      case 'month':
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      case 'week':
        const weekStart = this.getWeekStart(now);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return date >= weekStart && date <= weekEnd;
      default:
        return false;
    }
  }
  
 
  
  // Для месяца и недели
  getPeriodAreaStyle(group: BookingGroup): any {
    if (!this.originalStartDate || !this.originalEndDate) {
      return { display: 'none' };
    }
    
    let startIndex = -1;
    let endIndex = -1;
    
    const startDate = new Date(this.originalStartDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(this.originalEndDate);
    endDate.setHours(0, 0, 0, 0);
    
    // Ищем начало и конец периода
    for (let i = 0; i < this.displayColumns.length; i++) {
      const colDate = new Date(this.displayColumns[i].fullDate);
      colDate.setHours(0, 0, 0, 0);
      
      // Начало: первая дата >= startDate
      if (startIndex === -1 && colDate.getTime() >= startDate.getTime()) {
        startIndex = i;
      }
      
      // Конец: последняя дата <= endDate
      if (colDate.getTime() <= endDate.getTime()) {
        endIndex = i;
      }
    }
    
    if (startIndex === -1 || endIndex === -1) {
      return { display: 'none' };
    }
    
    const columnPercent = 100 / this.displayColumns.length;
    const leftPercent = startIndex * columnPercent;
    const widthPercent = (endIndex - startIndex + 1) * columnPercent;
    
    const hasIntersection = this.hasGroupIntersection(group);
    const borderColor = hasIntersection ? '#dc3545' : '#28a745';
    const bgColor = hasIntersection ? 'rgba(220, 53, 69, 0.15)' : 'rgba(40, 167, 69, 0.15)';
    
    return {
      left: `${leftPercent}%`,
      width: `${widthPercent}%`,
      borderLeft: `2px solid ${borderColor}`,
      borderRight: `2px solid ${borderColor}`,
      backgroundColor: bgColor,
      display: 'block'
    };
  }
  
  // Для года
  getYearPeriodAreaStyle(group: BookingGroup): any {
    if (!this.originalStartDate || !this.originalEndDate) {
      return { display: 'none' };
    }
    
    const startDate = new Date(this.originalStartDate);
    const endDate = new Date(this.originalEndDate);
    
    let startMonthIndex = -1;
    let endMonthIndex = -1;
    
    // Ищем начало и конец периода среди месяцев
    for (let i = 0; i < this.displayColumns.length; i++) {
      const monthDate = this.displayColumns[i].fullDate;
      
      // Начало: первый месяц, где год и месяц >= startDate
      if (startMonthIndex === -1) {
        if (monthDate.getFullYear() > startDate.getFullYear() ||
            (monthDate.getFullYear() === startDate.getFullYear() && monthDate.getMonth() >= startDate.getMonth())) {
          startMonthIndex = i;
        }
      }
      
      // Конец: последний месяц, где год и месяц <= endDate
      if (monthDate.getFullYear() < endDate.getFullYear() ||
          (monthDate.getFullYear() === endDate.getFullYear() && monthDate.getMonth() <= endDate.getMonth())) {
        endMonthIndex = i;
      }
    }
    
    if (startMonthIndex === -1 || endMonthIndex === -1) {
      return { display: 'none' };
    }
    
    const columnPercent = 100 / this.displayColumns.length;
    const leftPercent = startMonthIndex * columnPercent;
    const widthPercent = (endMonthIndex - startMonthIndex + 1) * columnPercent;
    
    const hasIntersection = this.hasGroupIntersection(group);
    const borderColor = hasIntersection ? '#dc3545' : '#28a745';
    const bgColor = hasIntersection ? 'rgba(220, 53, 69, 0.15)' : 'rgba(40, 167, 69, 0.15)';
    
    return {
      left: `${leftPercent}%`,
      width: `${widthPercent}%`,
      borderLeft: `2px solid ${borderColor}`,
      borderRight: `2px solid ${borderColor}`,
      backgroundColor: bgColor,
      display: 'block'
    };
  }
}